<template>
  <VModal :showing="open" container-classes="max-w-2xl" @update:showing="open = $event">
    <template #default>
      <div class="flex flex-col gap-6 overflow-visible relative">
        <!-- Loading overlay -->
        <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
        <!-- Header -->
        <div>
          <div class="text-lg font-semibold">Share 3D Presentation</div>
          <p class="text-sm text-muted-foreground">
            Invite others to view or collaborate on this 3D Presentation by sharing the link or managing access below.
          </p>
        </div>

        <!-- Invite -->
        <div v-if="canManageAccess" class="flex justify-between gap-2 relative">
          <DeckHeaderShareInvite v-model:loading="loading" :deck="deck" :existing-users="users" @user-invited="fetchDeckPermissions" />
        </div>

        <!-- Access list -->
        <div>
          <div class="text-sm font-semibold mb-2">People with Access</div>
          <div
            class="space-y-4 h-52 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 hover_scrollbar-thumb-gray-400"
          >
            <div v-for="user in sortedUsers" :key="user.email" class="flex items-center gap-2">
              <div class="w-12 h-12 flex justify-center items-center text-white rounded-full bg-gray-900">
                <span>{{ user.initials }}</span>
              </div>
              <div class="text-sm min-w-0 flex-1">
                <span class="truncate block">{{ user.name }} {{ user.isCurrentUser ? '(you)' : '' }}</span>
                <span v-if="user.email" class="text-muted-foreground truncate block">{{ user.email }}</span>
              </div>
              <div class="flex items-center gap-2 ml-auto mr-2">
                <VDropdown
                  :options="roleOptions"
                  :model-value="getRoleOption(user.role)"
                  :disabled="shouldDisableRoleDropdown(user)"
                  size="sm"
                  @update:model-value="handleUserRoleSelect({ user, role: $event.id })"
                >
                  <template #current-single="{ item }">
                    <span class="block text-primary">{{ item.name }}</span>
                  </template>
                  <template #option="{ item }">
                    <VDropdownItem :item="item.name" />
                  </template>
                </VDropdown>

                <VButton
                  v-if="canRemoveSelf(user)"
                  variant="ghost"
                  size="sm"
                  :disabled="loading"
                  class="rounded-full border w-6 h-6 p-0 flex items-center justify-center"
                  @click="handleDeleteUser(user)"
                >
                  <FontAwesomeIcon icon="times" class="w-3 h-3" />
                </VButton>
              </div>
            </div>
          </div>
        </div>

        <!-- General Access -->
        <div>
          <div class="text-sm font-semibold mb-2">General Access</div>

          <VDropdown
            :options="generalAccessDropdownOptions"
            :model-value="getGeneralAccessOption(selectedGeneralAccess)"
            :disabled="!canManageAccess"
            class="w-full h-8 mb-4"
            dropdown-class="z-50"
            @update:model-value="handleGeneralAccessSelect(getGeneralAccessOptionById($event.id))"
          >
            <template #current-single="{ item }">
              <div class="flex items-center">
                <AccessTypeIcon :access-type="item.id as DeckBaseAccess" class="mr-2 w-4 h-4" />
                <span>{{ item.name }}</span>
              </div>
            </template>
            <template #option="{ item }">
              <div class="flex items-center px-4">
                <AccessTypeIcon :access-type="item.id as DeckBaseAccess" class="mr-2 w-4 h-4" />
                <span>{{ item.name }}</span>
              </div>
            </template>
          </VDropdown>
        </div>

        <div
          v-if="showMultipleOwnersBanner"
          class="flex items-center gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800"
        >
          <FontAwesomeIcon icon="exclamation-triangle" class="w-8 h-8" />
          <span class="text-sm">Add a company admin or co-owner to avoid potential access blockages.</span>
        </div>

        <!-- Footer -->
        <div class="flex justify-between">
          <VCopyButton
            :copy-text="shareUrl"
            variant="outline"
            @click="$analytics.track('deckShareDialogCopyLink', { clickedFromDialog: true })"
          >
            <FontAwesomeIcon icon="copy" class="w-4 h-4 mr-2" />
            Copy Link
          </VCopyButton>
          <VButton @click="open = false">Done</VButton>
        </div>
      </div>
    </template>
  </VModal>

  <!-- Embed Usage Warning Modal -->
  <EditorDeckModalsEmbedDeckUsage
    :showing="embedUsageWarning.showing"
    :decks-affected="embedUsageWarning.decksAffected"
    :public-decks="embedUsageWarning.publicDecks"
    @cancel="embedUsageWarning.cancel"
    @confirm="embedUsageWarning.confirm"
  />

  <!-- Drill Data Access Type Warning Modal -->
  <VModal :showing="drillDataAccessTypeWarning.showing" @update:showing="drillDataAccessTypeWarning.showing = $event">
    <template #header>
      <h2 class="mb-2 text-xl">Drill Data Access Warning</h2>
      <p class="m-0 text-sm font-light text-gray-800">This 3D Presentation contains drill data that will be affected by this change.</p>
    </template>
    <template #default>
      <div class="space-y-4">
        <p class="text-sm">
          This 3D presentation contains drill data that is currently private or secure. Making this presentation public will also make the
          drill data public.
        </p>
        <div v-if="drillDataAccessTypeWarning.drillData.length" class="space-y-2">
          <h3 class="text-sm font-semibold">Affected Drill Data:</h3>
          <ul class="text-sm space-y-1">
            <li v-for="drill in drillDataAccessTypeWarning.drillData" :key="drill.id">
              {{ drill.name }}
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <VButton variant="outline" @click="drillDataAccessTypeWarning.cancel">Cancel</VButton>
        <VButton variant="outline" @click="drillDataAccessTypeWarning.secondaryAction">Continue Without Making Drill Data Public</VButton>
        <VButton @click="drillDataAccessTypeWarning.confirm">Make Drill Data Public & Continue</VButton>
      </div>
    </template>
  </VModal>
</template>

<script setup lang="ts">
import { DeckBaseAccess, DeckPermissionsUserRole } from '~~/entities/deck'
import type { APIDeckPermissions, APIDeckPermissionsRole } from '~~/entities/api'
// import { useConfirmation } from '~/composables/useConfirmation'
// import { getParticipantColor, cleanupParticipantColors } from '~~/lib/participant-colors'
import * as Sentry from '@sentry/vue'
import type { DeepReadonly } from 'vue'
import type { EditDeck } from '~/composables/useEditDeck'
import type { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'
import type { DrillDataSet } from '~/entities/drillLabel'
import type { Drill } from '~/entities/drill'
import type { DeckWithEmbed } from '~/entities/embed'
import type { Deck, Slide } from '~/entities/deck'
import { USER_ROLES } from '~/lib/constants/user-roles'

interface User extends APIDeckPermissionsRole {
  initials: string
  isCurrentUser: boolean
  avatarColor: string
}

interface GeneralAccessOption {
  label: string
  value: DeckBaseAccess
}

const props = defineProps<{
  deck: DeepReadonly<EditDeck>
  slides?: DeepReadonly<Slide[]>
  drillDataSetManager?: DrillDataSetManager
}>()

const open = defineModel<boolean>('open', { default: false })

const emit = defineEmits<{
  'refresh-deck': []
}>()

// const { selectedCompany } = useCompanies()
const { getDeckPermissions, updateDeckPermissions, upsertDeckUserPermissions, deleteDeckUserPermissions } = useDeckPermissions()
const { state: userState, hasRole } = useUser()
const { setNotification } = useGlobalNotification()
// const { confirmation } = useConfirmation()
const { $analytics } = useNuxtApp()

const router = useRouter()
const loading = ref(false)
const users = ref<User[]>([])
const permissions = ref<APIDeckPermissions | null>(null)
const selectedGeneralAccess = ref<GeneralAccessOption | null>(null)
const userDeckRole = ref<DeckPermissionsUserRole | null>(null)
const showEmbedWarning = ref(false)
const showDrillDataWarning = ref(false)
const pendingAccessChange = ref<GeneralAccessOption | null>(null)
const embeds = ref<any[]>([])
const embedUsageWarning = reactive<{
  showing: boolean
  decksAffected: DeckWithEmbed[]
  publicDecks: any[]
  cancel(): void
  confirm(): void
}>({
  showing: false,
  decksAffected: [],
  publicDecks: [],
  cancel: () => {},
  confirm: () => {},
})
const drillDataAccessTypeWarning = reactive<{
  showing: boolean
  drillData: Drill[]
  cancel(): void
  confirm(): void
  secondaryAction(): void
}>({
  showing: false,
  drillData: [],
  cancel: () => {},
  confirm: () => {},
  secondaryAction: () => {},
})

const shareUrl = computed(() => {
  if (process.client) {
    const route = router.resolve({
      name: 'decks-id',
      params: { id: `${props.deck.id}` },
    })

    return `${window.location.origin}${route.fullPath}`
  }

  return ''
})

const generalAccessOptions = [
  {
    label: 'Only people invited',
    value: DeckBaseAccess.Private,
  },
  {
    // label: `Everyone at ${selectedCompany.value?.name ?? 'your company'} can view`,
    label: `Everyone at your company can view`,
    value: DeckBaseAccess.CompanyView,
  },
  {
    // label: `Everyone at ${selectedCompany.value?.name ?? 'your company'} can edit`,
    label: `Everyone at your company can edit`,
    value: DeckBaseAccess.CompanyEdit,
  },
  {
    label: 'Anyone with the link',
    value: DeckBaseAccess.Public,
  },
]

// Convert role enum to dropdown options
const roleOptions = computed(() =>
  Object.values(DeckPermissionsUserRole).map(role => ({
    id: role,
    name: role.charAt(0).toUpperCase() + role.slice(1),
  })),
)

// Convert general access options to dropdown format
const generalAccessDropdownOptions = computed(() =>
  generalAccessOptions.map(option => ({
    id: option.value,
    name: option.label,
    value: option.value,
  })),
)

function getRoleOption(role: DeckPermissionsUserRole) {
  return roleOptions.value.find(option => option.id === role) || null
}

function getGeneralAccessOption(option: GeneralAccessOption | null) {
  if (!option) return null
  return generalAccessDropdownOptions.value.find(opt => opt.id === option.value) || null
}

function getGeneralAccessOptionById(id: string): GeneralAccessOption | null {
  return generalAccessOptions.find(option => option.value === id) || null
}

// Sort users with current user first, then alphabetically
const sortedUsers = computed(() => {
  return [...users.value].sort((a, b) => {
    if (a.isCurrentUser && !b.isCurrentUser) return -1
    if (!a.isCurrentUser && b.isCurrentUser) return 1
    return a.name?.localeCompare(b.name ?? '') ?? 0
  })
})
const ownerCount = computed(() => {
  return users.value.filter(user => user.role === DeckPermissionsUserRole.Owner).length
})

const canManageAccess = computed(() => {
  return userDeckRole.value === DeckPermissionsUserRole.Owner || hasRole('super_admin')
})

const shouldDisableRoleDropdown = computed(() => (user: User) => {
  if (user.isCurrentUser && user.role === DeckPermissionsUserRole.Owner && ownerCount.value === 1) {
    return true
  }

  return loading.value || !canManageAccess.value
})

const canRemoveSelf = computed(() => (user: User) => {
  if (user.isCurrentUser && hasRole('company_admin')) {
    return false
  }

  if (user.isCurrentUser && user.role === DeckPermissionsUserRole.Owner && ownerCount.value === 1) {
    return false
  }

  return canManageAccess.value
})

const showMultipleOwnersBanner = computed(() => {
  const hasMultipleOwners = users.value.filter(user => user.role === DeckPermissionsUserRole.Owner).length > 1
  const isPrivate = selectedGeneralAccess.value?.value === DeckBaseAccess.Private
  return !loading.value && !hasMultipleOwners && isPrivate
})

async function handleGeneralAccessSelect(option: GeneralAccessOption | null) {
  if (!option) return

  $analytics.track('deckShareDialogGeneralAccessSelect', { option: option?.value })
  const currentAccess = selectedGeneralAccess.value?.value
  const isGoingFromPublicToPrivate = currentAccess === DeckBaseAccess.Public && option.value !== DeckBaseAccess.Public

  if (isGoingFromPublicToPrivate) {
    pendingAccessChange.value = option
    await openEmbedWarningDialog()
  } else {
    await applyAccessChange(option)
  }
}

async function applyAccessChange(option: GeneralAccessOption) {
  try {
    loading.value = true
    selectedGeneralAccess.value = option
    await updateDeckPermissions({
      deckId: props.deck.id,
      companyId: props.deck.company_id,
      baseAccess: option.value,
    })
    await fetchDeckPermissions()
    emit('refresh-deck')
    setNotification({
      type: 'success',
      title: 'General Access Updated',
      text: `The general access for the presentation has been updated`,
      delay: 5000,
    })
  } catch (error) {
    setNotification({
      type: 'danger',
      title: 'Error',
      text: 'Failed to update general access. Please try again.',
      delay: 5000,
    })
    Sentry.captureException(error)
  } finally {
    loading.value = false
  }
}

function cancelAccessChange() {
  showEmbedWarning.value = false
  pendingAccessChange.value = null
}

async function confirmAccessChange() {
  showEmbedWarning.value = false
  if (pendingAccessChange.value) {
    await applyAccessChange(pendingAccessChange.value)
    pendingAccessChange.value = null
  }
}

async function handleUserRoleSelect({ user, role }: { user: User; role: DeckPermissionsUserRole }) {
  $analytics.track('deckShareDialogUserRoleSelect', { user_id: user.user_id, role })
  const currentUserRole = users.value.find(u => u.user_id === user.user_id)?.role
  if (currentUserRole === DeckPermissionsUserRole.Editor && role === DeckPermissionsUserRole.Viewer && user.isCurrentUser) {
    // const confirmed = await confirmation({
    //   title: 'Remove Editor Access',
    //   description: 'By changing your access to Viewer you will not be able to revert back to Editor once saved.',
    //   submitText: 'Save',
    // })
    // if (!confirmed) return
  }

  try {
    loading.value = true
    await upsertDeckUserPermissions({
      deckId: props.deck.id,
      companyId: props.deck.company_id,
      user_id: user.user_id,
      role,
    })
    await fetchDeckPermissions()
    emit('refresh-deck')
    setNotification({
      type: 'success',
      title: 'User Role Updated',
      text: `The user role for ${user.name} has been updated`,
      delay: 5000,
    })
  } catch (error) {
    setNotification({
      type: 'danger',
      title: 'Error',
      text: 'Failed to update user role. Please try again.',
      delay: 5000,
    })
    Sentry.captureException(error)
  } finally {
    loading.value = false
  }
}

async function fetchDeckPermissions() {
  // Get the deck permissions
  permissions.value = await getDeckPermissions({
    deckId: props.deck.id,
    companyId: props.deck.company_id,
  })

  // Map the data for some convenience props
  const userIds = permissions.value?.roles.map(role => role.user_id).filter((id): id is number => id !== undefined) || []
  // cleanupParticipantColors(userIds)
  if (permissions.value) {
    users.value = permissions.value.roles.map(role => ({
      ...role,
      initials: role.name?.slice(0, 2).toUpperCase() ?? '',
      isCurrentUser: role.user_id === userState.value.user?.id,
      avatarColor: '',
    })) as any
  }

  // Set the user's deck role
  userDeckRole.value = users.value.find(user => user.isCurrentUser)?.role ?? null
}

async function handleDeleteUser(user: User) {
  // const confirmed = await confirmation({
  //   title: 'Delete User',
  //   description: `Deleting a user is a permanent action. Are you sure you want to delete ${user.name}?`,
  //   submitText: 'Delete',
  // })
  // if (!confirmed) return
  //
  try {
    loading.value = true
    await deleteDeckUserPermissions({
      deckId: props.deck.id,
      companyId: props.deck.company_id,
      user_id: user.user_id,
    })
    await fetchDeckPermissions()
    emit('refresh-deck')
    setNotification({
      type: 'success',
      title: 'User Removed',
      text: `The user ${user.name} has been removed from the presentation`,
      delay: 5000,
    })
  } catch (error) {
    setNotification({
      type: 'danger',
      title: 'Error',
      text: 'Failed to remove user. Please try again.',
      delay: 5000,
    })
    Sentry.captureException(error)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  loading.value = true

  // Fetch the deck permissions
  await fetchDeckPermissions()

  // Set the general access value to the deck's current general access
  selectedGeneralAccess.value = generalAccessOptions.find(option => option.value === permissions.value?.base_access) ?? null

  // Fetch embeds for this deck
  await fetchEmbeds()

  loading.value = false
})

async function fetchEmbeds() {
  try {
    const deckId = props.deck.id
    const companyId = props.deck.company_id
    if (deckId && companyId) {
      // const { getCompanyEmbedDecks } = useCompanyEmbedDecks(companyId)
      // embeds.value = await getCompanyEmbedDecks(deckId)
    }
  } catch (error) {
    embeds.value = []
    Sentry.captureException(error)
  }
}

const openEmbedWarningDialog = async () => {
  if (props.deck.id && props.deck.company_id) {
    try {
      loading.value = true
      const decksAffected = await getUsedEmbedDecks({ deckId: props.deck.id, companyId: props.deck.company_id })
      const publicDecks = await getPublicDecks(props.deck.company_id)

      if (decksAffected.length) {
        Object.assign(embedUsageWarning, {
          showing: !!decksAffected.length,
          decksAffected,
          publicDecks,
          confirm: async (selectedDeck: any) => {
            try {
              if (selectedDeck) {
                const replacementDeck = publicDecks.find(d => d.id === selectedDeck.id)
                if (replacementDeck) {
                  await updateAllEmbeds({ decks: decksAffected, deck: replacementDeck })
                }
              }
              embedUsageWarning.showing = false
              await openDrillLabelWarningDialog()
            } catch (e) {
              Sentry.captureException(e)
            }
          },
          cancel: () => {
            embedUsageWarning.showing = false
            pendingAccessChange.value = null
          },
        })
        return
      }

      await openDrillLabelWarningDialog()
    } finally {
      loading.value = false
    }
  }
}

const openDrillLabelWarningDialog = async () => {
  if (!props.drillDataSetManager || !props.slides) {
    // If no drill data manager or slides, proceed with access change
    if (pendingAccessChange.value) {
      await applyAccessChange(pendingAccessChange.value)
      pendingAccessChange.value = null
    }
    return
  }

  try {
    loading.value = true
    const modelIds = extractModelIds()
    const privateOrSecureDrillData: Drill[] = Array.from(
      new Set(
        (await getDrillLabels(modelIds))
          .filter(drill => drill.data_set.view_access_type === 'private' || drill.data_set.view_access_type === 'secure')
          .map(drill => JSON.stringify(drill.data_set)),
      ),
    ).map(x => JSON.parse(x))

    if (privateOrSecureDrillData.length) {
      Object.assign(drillDataAccessTypeWarning, {
        showing: true,
        drillData: privateOrSecureDrillData,
        cancel: () => {
          drillDataAccessTypeWarning.showing = false
          pendingAccessChange.value = null
        },
        confirm: async () => {
          try {
            await setDrillDataPublic(privateOrSecureDrillData)
            drillDataAccessTypeWarning.showing = false
            if (pendingAccessChange.value) {
              await applyAccessChange(pendingAccessChange.value)
              pendingAccessChange.value = null
            }
          } catch (e) {
            Sentry.captureException(e)
          }
        },
        secondaryAction: async () => {
          drillDataAccessTypeWarning.showing = false
          if (pendingAccessChange.value) {
            await applyAccessChange(pendingAccessChange.value)
            pendingAccessChange.value = null
          }
        },
      })

      return
    }

    if (pendingAccessChange.value) {
      await applyAccessChange(pendingAccessChange.value)
      pendingAccessChange.value = null
    }
  } finally {
    loading.value = false
  }
}

async function getDrillLabels(modelIds: number[]) {
  let dataSets: DrillDataSet[] = []
  if (props.deck.company_id && props.drillDataSetManager) {
    dataSets = (
      await Promise.all(
        modelIds.map(modelId => {
          return props.drillDataSetManager!.getDataSets({ modelId, companyId: props.deck.company_id })
        }),
      )
    ).flatMap(x => x)
  }
  return dataSets
}

function extractModelIds() {
  if (!props.slides) return []
  return [...new Set(props.slides.map(s => s.model_id).filter(x => !!x))] as number[]
}

async function setDrillDataPublic(drillData: Drill[]) {
  await Promise.all(
    drillData.map(async drill => {
      await $fetch<any>(`v2/drill/companies/${props.deck.company_id}/data/${drill.id}`, {
        ...useFetchSharedOptions(),
        method: 'PATCH',
        body: {
          view_access_type: 'public',
        },
      })
    }),
  )
}

async function getUsedEmbedDecks({ deckId, companyId }: { deckId: string | number; companyId: string | number }) {
  const { results } = await $fetch<any>(`v2/companies/${companyId}/embed/decks`, {
    ...useFetchSharedOptions(),
    params: { deck_id: deckId, include: ['partner_name'] },
  })
  return results
}

async function getPublicDecks(companyId: string | number) {
  const { results } = await $fetch<{ results: Deck[] }>(`v2/companies/${companyId}/decks`, {
    ...useFetchSharedOptions(),
    params: {
      base_access: 'public',
      archived: false,
      include: ['slides'],
    },
  })

  // Filter decks that have exactly 2 M slides OR exactly 2 W slides
  return results.filter(deck => {
    const deckMSlides = deck.slides?.filter(slide => slide.type === 'M' && slide.model_id) || []
    const deckWSlides = deck.slides?.filter(slide => slide.type === 'W' && slide.asset_id) || []

    // Check for exactly 2 M slides OR exactly 2 W slides
    return deckMSlides.length === 2 || deckWSlides.length === 2
  })
}

async function updateAllEmbeds({ decks, deck }: { decks: DeckWithEmbed[]; deck: any }) {
  const [start_slide_id, end_slide_id] = getDeckSlideIds(deck)

  await Promise.all(
    decks.map(async (embeddedDeck: DeckWithEmbed) => {
      await $fetch<any>(`v2/companies/${deck.company_id}/embed/decks/${embeddedDeck.id}`, {
        ...useFetchSharedOptions(),
        method: 'PATCH',
        body: {
          start_slide_id,
          end_slide_id,
          company_id: deck.company_id,
          deck_id: deck.id,
        },
      })
    }),
  )
}

function getDeckSlideIds(deck: any) {
  const start_slide_id = deck.slides?.find((slide: any) => slide.type === 'M')?.id
  const end_slide_id = deck.slides?.find((slide: any) => slide.id !== start_slide_id && slide.type === 'M')?.id

  return [start_slide_id, end_slide_id]
}
</script>
